/**
 * Применение темы к документу (ТЗ UX-01).
 *
 * Выбор пользователя хранится в пространстве, а применяется атрибутом на
 * корневом элементе. Значение `system` в разметку не пишется вовсе: без
 * атрибута работает `prefers-color-scheme`, и тема следует за системой сама.
 */

import { useEffect, useState } from 'react';
import type { ResolvedTheme, ThemePreference } from '@type2x/shared-contracts';
import { THEME_ATTRIBUTE, resolveTheme } from '@type2x/shared-contracts';

/** Запрос системной тёмной темы. */
const DARK_QUERY = '(prefers-color-scheme: dark)';

/**
 * Список медиазапроса, если браузер его поддерживает.
 *
 * В jsdom `matchMedia` нет, а в старых webview он есть без `addEventListener`.
 * Отсутствие поддержки — не ошибка: тема просто остаётся светлой, пока
 * пользователь не выберет её явно.
 */
function darkMediaQuery(): MediaQueryList | null {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
  return window.matchMedia(DARK_QUERY);
}

/** Следит за системной настройкой тёмной темы. */
export function useSystemPrefersDark(): boolean {
  const [prefersDark, setPrefersDark] = useState(() => darkMediaQuery()?.matches ?? false);

  useEffect(() => {
    const query = darkMediaQuery();
    if (query === null || typeof query.addEventListener !== 'function') return;

    const onChange = (event: MediaQueryListEvent): void => setPrefersDark(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return prefersDark;
}

/**
 * Применяет выбранную тему к корневому элементу документа.
 *
 * @param preference выбор пользователя; null — пространство ещё не загружено.
 * @returns тема, которая применилась на самом деле.
 */
export function useTheme(preference: ThemePreference | null): ResolvedTheme {
  const systemPrefersDark = useSystemPrefersDark();
  const chosen = preference ?? 'system';

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;

    // Атрибут снимается, а не переписывается значением `system`: его
    // отсутствие и означает «как в системе», и селектор темы это учитывает.
    if (chosen === 'system') root.removeAttribute(THEME_ATTRIBUTE);
    else root.setAttribute(THEME_ATTRIBUTE, chosen);

    return () => root.removeAttribute(THEME_ATTRIBUTE);
  }, [chosen]);

  return resolveTheme(chosen, systemPrefersDark);
}
